import { h, Host } from "@stencil/core";
import { debounce } from "../../utils/debounce";
import { getViewportWidth } from "../../utils/getViewportWidth";
export class Tooltip {
    constructor() {
        this.tooltipTarget = "";
        this.align = "right";
        this.controlled = false;
        this.tooltipAlignment = undefined;
        this.tooltipTipAlignment = undefined;
        this.tooltipPosition = {
            top: 0,
            left: 0
        };
        this.textOnlyMode = undefined;
        this.uniqueId = undefined;
        this.isTooltipActive = false;
        this.calculateTooltipMaxHeight = false;
        this.calculateArrowPosition = false;
        this.isPositioned = undefined;
    }
    /** the target of the tooltip, hover (or click) on the element activates the tooltip */
    tooltipTargetElement;
    /** indicates when the tooltip was last activated, used to provide a more accurate
     * timeout for deactivation.
     */
    lastActivated;
    /** the tip of the tooltip is positioned on the corner of the tooltip target */
    positionTipOnCorner = false;
    /** the tooltip message itself */
    tooltipElement;
    /** used to calculate whether the tooltip can be placed in a certain alignment or not,
     * added to the dimensions of the tooltip message to give some extra space around it
     * this is overwritten by the css variable --ux-tooltip-safe-margin
     */
    safeMargin = 0;
    tooltipDimensions;
    /** ux-tooltip element */
    element;
    /** DOMRects of the tooltip target */
    tooltipTargetRects;
    setControlled() {
        if (!this.controlled) {
            this.addEventListeners();
        }
        else {
            this.removeEventListeners();
        }
    }
    calculatePosition() {
        if (this.isTooltipActive) {
            this.calculateTooltipPosition();
            window.addEventListener("scroll", this.scrollHandler);
        }
        else {
            window.removeEventListener("scroll", this.scrollHandler);
            this.tooltipAlignment = undefined;
        }
    }
    /** show/activate the tooltip */
    async showTooltip() {
        if (!this.isTooltipActive) {
            this.activateTooltip();
        }
    }
    /** hide/deactivate the tooltip */
    async hideTooltip() {
        this.isTooltipActive = false;
    }
    connectedCallback() {
        this.uniqueId = `ux-tooltip-${idCounter++}`;
    }
    componentWillLoad() {
        this.textOnlyMode = !this.element.firstElementChild;
    }
    findTargetElement() {
        let target = null;
        let current = this.element.parentElement || this.element.parentNode; // DOM | ShadowDOM
        while (target === null && current !== null) {
            target = current.querySelector(this.tooltipTarget);
            current = current.parentElement;
        }
        return target;
    }
    componentDidLoad() {
        let timeout = 0;
        if (document.querySelector(this.tooltipTarget) === null) {
            timeout = 500;
        }
        // this timeout ensures the DOM is ready
        setTimeout(() => {
            this.tooltipTargetElement = this.findTargetElement();
            if (this.tooltipTargetElement === null) {
                return;
            }
            if (!this.controlled) {
                this.tooltipTargetElement.addEventListener("mouseenter", this.activateTooltip);
                this.tooltipElement?.addEventListener("mouseenter", this.activateTooltip);
                this.tooltipTargetElement.addEventListener("mouseleave", this.deactivateTooltip);
                this.tooltipElement?.addEventListener("mouseleave", this.deactivateTooltip);
                this.tooltipElement?.addEventListener("click", this.handleTooltipClick);
            }
        }, timeout);
        this.positionTipOnCorner =
            window.getComputedStyle(this.element).getPropertyValue("--ux-tooltip-tip-positioning") === "corner";
        this.safeMargin = parseInt(window.getComputedStyle(this.element).getPropertyValue("--ux-tooltip-safe-margin"), 10);
    }
    scrollHandler = () => {
        this.calculateTooltipPosition();
    };
    handleTooltipClick = (e) => {
        if (e.target.hasAttribute("data-ux-tooltip-close")) {
            this.isTooltipActive = false;
        }
    };
    activateTooltip = () => {
        if (!this.isTooltipActive) {
            this.activatedTooltipEvent.emit(this.uniqueId);
        }
        this.isTooltipActive = true;
        this.lastActivated = new Date().getTime();
    };
    /** mainly for mobile: click on the target will open the tooltip,
     * click outside of tooltip self will close it
     */
    handleClicks(e) {
        if (this.controlled) {
            return;
        }
        if (!this.tooltipTargetElement) {
            return;
        }
        if (this.tooltipTargetElement.contains(e.target)) {
            if (!this.isTooltipActive) {
                this.isTooltipActive = true;
            }
        }
        else if (e.target.closest("div.box") === null) {
            this.isTooltipActive = false;
        }
    }
    calculateTooltipPosition = debounce(() => {
        if (!this.tooltipTargetElement || !this.tooltipElement) {
            return;
        }
        this.tooltipDimensions = this.tooltipElement.getBoundingClientRect();
        this.tooltipTargetRects = this.tooltipTargetElement.getBoundingClientRect();
        const invalidTop = this.tooltipTargetRects.top < this.tooltipDimensions.height + this.safeMargin;
        const invalidBottom = window.innerHeight - this.tooltipTargetRects.bottom < this.tooltipDimensions.height + this.safeMargin;
        const verticalMiddle = this.tooltipTargetRects.top + 0.5 * this.tooltipTargetRects.height;
        const horizontalMiddle = this.tooltipTargetRects.left + 0.5 * this.tooltipTargetRects.width;
        const invalidSide = this.tooltipTargetRects.bottom - this.tooltipTargetRects.height / 2 < this.tooltipDimensions.height / 2 ||
            window.innerHeight - this.tooltipTargetRects.bottom - this.tooltipTargetRects.height / 2 <
                this.tooltipDimensions.height / 2;
        const invalidLeft = this.safeMargin + this.tooltipDimensions.width > this.tooltipTargetRects.left || invalidSide;
        const invalidRight = this.safeMargin + this.tooltipDimensions.width > getViewportWidth() - this.tooltipTargetRects.right || invalidSide;
        // responsive styleguide needs the tip on the right/left corner on the top or bottom of the tooltip
        if (this.positionTipOnCorner) {
            // if the tooltip target is positioned more than 50% from left, the tooltip will be positioned on the right
            if (horizontalMiddle > getViewportWidth() / 2) {
                this.tooltipTipAlignment = "right";
            }
            else {
                this.tooltipTipAlignment = "left";
            }
            if (this.align === "bottom" && !invalidBottom) {
                return this.setTooltipToBottomEdge(horizontalMiddle);
            }
            if (this.align === "top" && !invalidTop) {
                return this.setTooltipToTopEdge(horizontalMiddle);
            }
            // fallback if align is not set
            if (!invalidBottom) {
                return this.setTooltipToBottomEdge(horizontalMiddle);
            }
            if (!invalidTop) {
                return this.setTooltipToTopEdge(horizontalMiddle);
            }
            return this.setTooltipToBottomEdge(horizontalMiddle);
        }
        if (this.align === "top" && !invalidTop) {
            return this.setTooltipToTop(horizontalMiddle);
        }
        if (this.align === "bottom" && !invalidBottom) {
            return this.setTooltipToBottom(horizontalMiddle);
        }
        if (this.align === "left" && !invalidLeft) {
            return this.setTooltipToLeft(verticalMiddle);
        }
        if (this.align === "right" && !invalidRight) {
            return this.setTooltipToRight(verticalMiddle);
        }
        // try for valid Fallbacks
        if (!invalidTop) {
            return this.setTooltipToTop(horizontalMiddle);
        }
        if (!invalidBottom) {
            return this.setTooltipToBottom(horizontalMiddle);
        }
        if (!invalidRight) {
            return this.setTooltipToRight(verticalMiddle);
        }
        if (!invalidLeft) {
            return this.setTooltipToLeft(verticalMiddle);
        }
        // Fallback if there is no space anywhere
        return this.setTooltipToBottom(horizontalMiddle);
    }, 100);
    deactivateTooltip = async () => {
        const deactivationTimeoutStart = new Date().getTime();
        setTimeout(() => {
            if (this.lastActivated < deactivationTimeoutStart) {
                this.isTooltipActive = false;
            }
        }, 700);
    };
    setTooltipPosition = ({ top, left }) => {
        if (top) {
            this.element.style.setProperty("--ux-tooltip-box-top", `${top}px`);
        }
        if (left) {
            this.element.style.setProperty("--ux-tooltip-box-left", `${left}px`);
        }
    };
    /** setting the tooltip in different positions */
    setTooltipToRight = (verticalMiddle) => {
        this.tooltipAlignment = "right";
        this.setTooltipPosition({
            top: verticalMiddle,
            left: this.tooltipTargetRects.right + this.safeMargin
        });
    };
    setTooltipToLeft = (verticalMiddle) => {
        this.tooltipAlignment = "left";
        this.setTooltipPosition({
            top: verticalMiddle,
            left: this.tooltipTargetRects.left - this.safeMargin
        });
    };
    setTooltipToTop = (horizontalMiddle) => {
        this.tooltipAlignment = "top";
        const willOverflowLeft = horizontalMiddle < this.tooltipDimensions.width / 2;
        const willOverflowRight = getViewportWidth() - horizontalMiddle < this.tooltipDimensions.width / 2;
        const leftPosition = !willOverflowLeft && !willOverflowRight
            ? horizontalMiddle
            : willOverflowLeft
                ? 20 + this.tooltipDimensions.width / 2
                : window.innerWidth - 30 - this.tooltipDimensions.width / 2;
        this.setTooltipPosition({
            top: this.tooltipTargetRects.top - this.safeMargin,
            left: leftPosition
        });
        this.calculateArrowPosition = true;
    };
    /** bottom alignment will also cause an adjustment to the tooltip
     * pointer arrow position if necessary.
     */
    setTooltipToBottom = (horizontalMiddle) => {
        this.tooltipAlignment = "bottom";
        const willOverflowLeft = horizontalMiddle < this.tooltipDimensions.width / 2;
        const willOverflowRight = getViewportWidth() - horizontalMiddle < this.tooltipDimensions.width / 2;
        const leftPosition = !willOverflowLeft && !willOverflowRight
            ? horizontalMiddle
            : willOverflowLeft
                ? 20 + this.tooltipDimensions.width / 2
                : window.innerWidth - 30 - this.tooltipDimensions.width / 2;
        this.setTooltipPosition({
            top: this.tooltipTargetRects.bottom + this.safeMargin,
            left: leftPosition
        });
        this.calculateTooltipMaxHeight = true;
        this.calculateArrowPosition = true;
    };
    setTooltipToTopEdge = (horizontalMiddle) => {
        this.tooltipAlignment = "top-edge";
        this.setTooltipPosition({
            top: this.tooltipTargetRects.top - this.tooltipDimensions.height - this.safeMargin,
            left: horizontalMiddle
        });
        this.calculateArrowPosition = true;
    };
    setTooltipToBottomEdge = (horizontalMiddle) => {
        this.tooltipAlignment = "bottom-edge";
        this.setTooltipPosition({
            top: this.tooltipTargetRects.bottom + this.safeMargin,
            left: horizontalMiddle
        });
        this.calculateArrowPosition = true;
    };
    componentDidRender() {
        if (!this.tooltipElement) {
            return;
        }
        if (this.calculateArrowPosition) {
            const tooltipRects = this.tooltipElement.getBoundingClientRect();
            const arrowPosition = this.tooltipTargetRects.left - tooltipRects.left + 18 + (this.tooltipTargetRects.width / 2 - 18);
            this.element.style.setProperty("--ux-tooltip-custom-tip-position", `${arrowPosition}px`);
            this.calculateArrowPosition = false;
        }
        if (this.calculateTooltipMaxHeight) {
            const tooltipRects = this.tooltipElement.getBoundingClientRect();
            const tooltipVerticalPadding = 60;
            const safeBottomDistance = 20;
            const tooltipMaxHeight = window.innerHeight - (tooltipRects.top + tooltipVerticalPadding + safeBottomDistance);
            // do not accept maxHeights less than 200: the user needs to scroll
            if (tooltipMaxHeight > 200 && this.tooltipAlignment === "bottom") {
                this.element.style.setProperty("--ux-tooltip-max-height", `${tooltipMaxHeight}px`);
                this.element.style.setProperty("--ux-tooltip-overflow-y", "auto");
            }
            else {
                this.element.style.removeProperty("--ux-tooltip-max-height");
                this.element.style.removeProperty("--ux-tooltip-overflow-y");
            }
        }
    }
    /** Preventing multiple Tooltip active at the same time */
    activatedTooltipEvent;
    preventMultipleTooltipsActive(event) {
        if (event.detail !== this.uniqueId) {
            this.isTooltipActive = false;
        }
    }
    disconnectedCallback() {
        if (!this.tooltipTargetElement) {
            return;
        }
        this.tooltipTargetElement.removeEventListener("mouseenter", this.activateTooltip);
        this.tooltipElement?.removeEventListener("mouseenter", this.activateTooltip);
        this.tooltipTargetElement.removeEventListener("mouseleave", this.deactivateTooltip);
        this.tooltipElement?.removeEventListener("mouseleave", this.deactivateTooltip);
        this.tooltipElement?.removeEventListener("click", this.handleTooltipClick);
    }
    addEventListeners = () => {
        this.removeEventListeners();
        if (this.tooltipTargetElement !== null) {
            this.tooltipTargetElement.addEventListener("mouseenter", this.activateTooltip);
            this.tooltipTargetElement.addEventListener("mouseleave", this.deactivateTooltip);
        }
        if (this.tooltipElement) {
            this.tooltipElement.addEventListener("mouseenter", this.activateTooltip);
            this.tooltipElement.addEventListener("mouseleave", this.deactivateTooltip);
            this.tooltipElement.addEventListener("click", this.handleTooltipClick);
        }
    };
    removeEventListeners = () => {
        window.removeEventListener("scroll", this.scrollHandler);
        if (this.tooltipTargetElement !== null) {
            this.tooltipTargetElement.removeEventListener("mouseenter", this.activateTooltip);
            this.tooltipTargetElement.removeEventListener("mouseleave", this.deactivateTooltip);
        }
        if (this.tooltipElement) {
            this.tooltipElement.removeEventListener("mouseenter", this.activateTooltip);
            this.tooltipElement.removeEventListener("mouseleave", this.deactivateTooltip);
            this.tooltipElement.removeEventListener("click", this.handleTooltipClick);
        }
    };
    render() {
        const { tooltipAlignment, tooltipTipAlignment, textOnlyMode } = this;
        const Wrapper = textOnlyMode ? "p" : "div";
        return (h(Host, { key: '3acc295247f662d84e3d19103683b9dfcf0f28eb', active: this.isTooltipActive }, h("div", { key: '3ed907ccdb422156ce7bef58bd14c752760b694b', class: {
                box: true,
                "box--active": this.isTooltipActive && tooltipAlignment !== undefined,
                [`box--${tooltipAlignment}`]: tooltipAlignment !== undefined,
                [`box--tip-${tooltipTipAlignment}`]: tooltipTipAlignment !== undefined
            }, ref: (el) => (this.tooltipElement = el) }, h("div", { key: 'db314a685a916c5321284416f175e5d3673272ca', class: {
                tip: true,
                [`tip--${this.tooltipTipAlignment}`]: this.tooltipTipAlignment !== undefined
            } }, h("div", { key: '78789414955bb864f35fdbda7f97fb4dd534e1b6', class: "tip-shadow" })), h(Wrapper, { key: 'ab2ee3f8cc208a4e52e73d3559720e91f4e10df9', class: {
                wrapper: true,
                "wrapper--text-only-mode": textOnlyMode
            } }, h("slot", { key: '0d0239026ba9e456fe8beef1e7d0926b18516644' })))));
    }
    static get is() { return "ux-tooltip"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["tooltip.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["tooltip.css"]
        };
    }
    static get properties() {
        return {
            "tooltipTarget": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "the css selector of the tooltip target, tooltip and target\nhave to have the same parent element"
                },
                "attribute": "tooltip-target",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "align": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"right\" | \"bottom\" | \"left\" | \"top\"",
                    "resolved": "\"bottom\" | \"left\" | \"right\" | \"top\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "the desired alignment of the tooltip message"
                },
                "attribute": "align",
                "reflect": false,
                "defaultValue": "\"right\""
            },
            "controlled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "set to true if the tooltip should be controlled from outside instead of handling UI events itself"
                },
                "attribute": "controlled",
                "reflect": false,
                "defaultValue": "false"
            }
        };
    }
    static get states() {
        return {
            "tooltipAlignment": {},
            "tooltipTipAlignment": {},
            "tooltipPosition": {},
            "textOnlyMode": {},
            "uniqueId": {},
            "isTooltipActive": {},
            "calculateTooltipMaxHeight": {},
            "calculateArrowPosition": {},
            "isPositioned": {}
        };
    }
    static get events() {
        return [{
                "method": "activatedTooltipEvent",
                "name": "uxTooltipActivated",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Preventing multiple Tooltip active at the same time"
                },
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                }
            }];
    }
    static get methods() {
        return {
            "showTooltip": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "show/activate the tooltip",
                    "tags": []
                }
            },
            "hideTooltip": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "hide/deactivate the tooltip",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "element"; }
    static get watchers() {
        return [{
                "propName": "controlled",
                "methodName": "setControlled"
            }, {
                "propName": "isTooltipActive",
                "methodName": "calculatePosition"
            }];
    }
    static get listeners() {
        return [{
                "name": "click",
                "method": "handleClicks",
                "target": "window",
                "capture": false,
                "passive": false
            }, {
                "name": "uxTooltipActivated",
                "method": "preventMultipleTooltipsActive",
                "target": "window",
                "capture": false,
                "passive": false
            }];
    }
}
/** this variable is incremented by each instance */
let idCounter = 0;
//# sourceMappingURL=tooltip.js.map
